import { action, computed, makeAutoObservable, runInAction } from 'mobx';
import { toast } from 'react-toastify';
import { NotificationListItem, notificationService } from 'services/notificationService';

export class NotificationStore {
  unreadNotifications: NotificationListItem[] = [];
  archivedNotifications: NotificationListItem[] = [];
  isLoadingUnread: boolean = false;
  isLoadingArchived: boolean = false;
  archivedPage: number = 1;
  archivedHasMore: boolean = true;
  archivedTotalCount: number = 0;
  error: string | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  /**
   * Computed property for unread count
   */
  @computed
  get unreadCount(): number {
    return this.unreadNotifications.length;
  }

  /**
   * Fetch unread notifications
   */
  fetchUnreadNotifications = async (): Promise<void> => {
    this.isLoadingUnread = true;
    this.error = null;

    try {
      const response = await notificationService.getUnreadNotifications();
      runInAction(() => {
        this.unreadNotifications = response.data || [];
        this.isLoadingUnread = false;
      });
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || 'Failed to fetch unread notifications';
        this.isLoadingUnread = false;
      });
      console.error('Error fetching unread notifications:', error);
      toast.error('Failed to load unread notifications');
    }
  };

  /**
   * Fetch archived notifications (with pagination support)
   * @param page - Page number (defaults to 1)
   * @param append - Whether to append to existing list (for infinite scroll)
   */
  fetchArchivedNotifications = async (page: number = 1, append: boolean = false): Promise<void> => {
    this.isLoadingArchived = true;
    this.error = null;

    try {
      const response = await notificationService.getArchivedNotifications(page, 20);
      runInAction(() => {
        if (append) {
          // Append for infinite scroll
          this.archivedNotifications = [...this.archivedNotifications, ...(response.data || [])];
        } else {
          // Replace for initial load or refresh
          this.archivedNotifications = response.data || [];
        }
        this.archivedPage = response.page;
        this.archivedHasMore = response.has_next;
        this.archivedTotalCount = response.count;
        this.isLoadingArchived = false;
      });
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || 'Failed to fetch archived notifications';
        this.isLoadingArchived = false;
      });
      console.error('Error fetching archived notifications:', error);
      toast.error('Failed to load archived notifications');
    }
  };

  /**
   * Load more archived notifications (for infinite scroll)
   */
  loadMoreArchived = async (): Promise<void> => {
    if (this.isLoadingArchived || !this.archivedHasMore) {
      return;
    }
    await this.fetchArchivedNotifications(this.archivedPage + 1, true);
  };

  /**
   * Mark a single notification as read
   */
  markNotificationAsRead = async (notificationId: number): Promise<void> => {
    try {
      const response = await notificationService.markNotificationRead(notificationId);

      runInAction(() => {
        // Remove from unread list
        this.unreadNotifications = this.unreadNotifications.filter(n => n.id !== notificationId);

        // Add to archived list if not already present
        const notification = response.data;
        const archivedItem: NotificationListItem = {
          id: notification.id,
          title: notification.title,
          message: notification.message,
          notification_type: notification.notification_type,
          is_read: notification.is_read,
          created_at: notification.created_at,
          metadata: notification.metadata,
        };

        // Check if already in archived list
        const exists = this.archivedNotifications.find(n => n.id !== notificationId);
        if (!exists) {
          // Add to beginning of archived list (most recent first)
          this.archivedNotifications = [archivedItem, ...this.archivedNotifications];
        }
      });
    } catch (error: any) {
      console.error('Error marking notification as read:', error);
      toast.error('Failed to mark notification as read');
      throw error;
    }
  };

  /**
   * Mark all notifications as read
   */
  markAllAsRead = async (): Promise<void> => {
    try {
      const _response = await notificationService.markAllNotificationsRead();

      runInAction(() => {
        // Move all unread to archived
        const _now = new Date().toISOString();
        const movedNotifications: NotificationListItem[] = this.unreadNotifications.map(n => ({
          ...n,
          is_read: true,
        }));

        // Add to beginning of archived list
        this.archivedNotifications = [...movedNotifications, ...this.archivedNotifications];

        // Clear unread list
        this.unreadNotifications = [];
      });

      // Refresh both lists to get updated data
      await Promise.all([
        this.fetchUnreadNotifications(),
        this.fetchArchivedNotifications(1, false),
      ]);
    } catch (error: any) {
      console.error('Error marking all notifications as read:', error);
      toast.error('Failed to mark all notifications as read');
      throw error;
    }
  };

  /**
   * Add notification from WebSocket
   * Determines which list to add to based on is_read property
   */
  addNotification = (notification: NotificationListItem): void => {
    // Check if notification already exists
    const existsInUnread = this.unreadNotifications.find(n => n.id === notification.id);
    const existsInArchived = this.archivedNotifications.find(n => n.id === notification.id);

    if (notification.is_read) {
      // Add to archived if not already present
      if (!existsInArchived) {
        this.archivedNotifications = [notification, ...this.archivedNotifications];
        // Sort by created_at descending
        this.archivedNotifications.sort(
          (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
      }
    } else {
      // Add to unread if not already present
      if (!existsInUnread) {
        this.unreadNotifications = [notification, ...this.unreadNotifications];
        // Sort by created_at descending
        this.unreadNotifications.sort(
          (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
      }
    }
  };

  /**
   * Reset store state
   */
  @action
  reset(): void {
    this.unreadNotifications = [];
    this.archivedNotifications = [];
    this.isLoadingUnread = false;
    this.isLoadingArchived = false;
    this.archivedPage = 1;
    this.archivedHasMore = true;
    this.archivedTotalCount = 0;
    this.error = null;
  }
}
